import {
  HttpClient,
  HttpContext,
  HttpErrorResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';

import {
  EMPTY,
  Observable,
  Subject,
  Subscription,
  Unsubscribable,
  catchError,
  concat,
  first,
  map,
  retryWhen,
  switchMap,
  tap,
} from 'rxjs';

import {
  AccountCertificate,
  AccountDTO,
  AccountMetricsResponse,
  AccountServiceError,
  AccountTariff,
  AccountsResponse,
  AttachPartnerRequest,
  CertificateModel,
  CreateAccountResponse,
  LeverageSettings,
  LeverageSettingsResponse,
  PartnerState,
  SwapfreeSettings,
  TradingAccount,
  accountURLs,
} from '@personal-area/global/accounts/models';
import {
  IS_API_GATEWAY,
  IS_PRIVATE_API,
} from '@personal-area/global/http/models';
import { retryStrategy } from '@personal-area/global/http/services';
import { Role } from '@personal-area/global/users/models';

import { AccountsFacade } from '../store/facades/account';
import { MtService } from './mt.service';

@Injectable({
  providedIn: 'root',
})
export class AccountService {
  public onOpenAccount: Subject<boolean>;
  public onError: Subject<HttpErrorResponse>;

  constructor(
    private _http: HttpClient,
    private _mtService: MtService,
    private _accountsFacade: AccountsFacade,
  ) {
    this.onOpenAccount = new Subject<boolean>();
    this.onError = new Subject<HttpErrorResponse>();
  }

  private _filterByTariffs(items: AccountDTO[]): AccountDTO[] {
    // FBSCAB-1131
    return items.filter(
      ({ tariff }) =>
        ![AccountTariff.ramadan, AccountTariff.ramadanReal].includes(tariff),
    );
  }

  /**
   * Подписка на событие логина.
   * @param {(result: boolean) => void} onOpenAccount Функция-подписчик на успешное событие
   * @param {(err: HttpErrorResponse) => void} onError Функция-подписчик на ошибку
   * @returns {AnonymousSubscription} Объект подписки
   */
  public subscribe(
    onOpenAccount: (result: boolean) => void,
    onError?: (err: HttpErrorResponse) => void,
  ): Unsubscribable {
    let onErrorSubs: Subscription;

    const onOpenAccountSubs = this.onOpenAccount.subscribe(onOpenAccount);

    // нужна обработка ошибок
    if (onError) {
      onErrorSubs = this.onError.subscribe(onError);
    }

    return {
      unsubscribe: (): void => {
        onOpenAccountSubs.unsubscribe();

        // есть подписчики на ошибки
        if (onErrorSubs) {
          onErrorSubs.unsubscribe();
        }
      },
    };
  }

  public fetchAccount(accountID: 'all' | number): Observable<AccountDTO> {
    return this._http.get<AccountDTO>(accountURLs.fetchAccount(accountID), {
      params: {
        withExtras: true,
      },
      context: new HttpContext()
        .set(IS_PRIVATE_API, true)
        .set(IS_API_GATEWAY, true),
    });
  }

  public fetchAccounts(): Observable<AccountsResponse> {
    return this._http
      .get<AccountsResponse>(accountURLs.fetchAccounts(), {
        params: {
          withExtras: true,
          types: [Role.Trader, Role.Partner, Role.Exchanger].join(','),
        },
        context: new HttpContext()
          .set(IS_PRIVATE_API, true)
          .set(IS_API_GATEWAY, true),
      })
      .pipe(
        map((resp: AccountsResponse) => ({
          ...resp,
          list: this._filterByTariffs(resp.list),
        })),
      );
  }

  public fetchMetrics(
    userId: number,
    accountIDs: number[],
  ): Observable<AccountMetricsResponse> {
    return this._http.post<AccountMetricsResponse>(
      accountURLs.fetchMetrics(userId),
      { accountIDs },
      {
        context: new HttpContext()
          .set(IS_PRIVATE_API, true)
          .set(IS_API_GATEWAY, true),
      },
    );
  }

  public openTraderAccount(
    userID: number,
    data: TradingAccount,
  ): Observable<CreateAccountResponse> {
    return this._http.post<CreateAccountResponse>(
      accountURLs.openTrader(userID),
      data,
      {
        context: new HttpContext()
          .set(IS_PRIVATE_API, true)
          .set(IS_API_GATEWAY, true),
      },
    );
  }

  public fetchCertificate(
    userID: number,
    accountID: number,
  ): Observable<CertificateModel> {
    return this._http.get<CertificateModel>(
      accountURLs.fetchCertificate(userID, accountID),
      {
        context: new HttpContext()
          .set(IS_PRIVATE_API, true)
          .set(IS_API_GATEWAY, true),
      },
    );
  }

  public getCachedCertificate(
    userID: number,
    accountID: number,
  ): Observable<AccountCertificate> {
    const certificateFromStore$ = this._accountsFacade
      .getCertificate$(accountID)
      .pipe(
        first(),
        catchError(() => EMPTY), // fallback if store is not ready yet; for example, registration zone
      );
    const certificateFromNetwork$ = this.fetchCertificate(
      userID,
      accountID,
    ).pipe(
      map((cert) => {
        if (!cert?.login) {
          // Try again after delay if the account is not ready yet
          throw new Error(AccountServiceError.NoCertificateLogin);
        }
        return cert;
      }),
      retryWhen(
        retryStrategy({
          maxRetryAttempts: 2,
          duration: 2000,
          isScalingDuration: false,
        }),
      ),
      catchError((err) => {
        if (err.status === 404) {
          throw new Error(AccountServiceError.NoCertificateLogin);
        }

        throw new Error(err.name);
      }),
      switchMap((cert) => this.extendCertWithServer(cert)),
      tap((certificate) =>
        this._accountsFacade.saveCertificates(certificate.id, certificate),
      ),
    );

    return concat(certificateFromStore$, certificateFromNetwork$).pipe(
      first(Boolean),
    );
  }

  public extendCertWithServer(
    cert: CertificateModel,
  ): Observable<AccountCertificate> {
    return this._mtService.getServer(cert.server).pipe(
      map(
        (server) =>
          <AccountCertificate>{
            ...cert,
            serverAddress: server.address,
            serverName: server.name,
            serverType: server.type,
          },
      ),
    );
  }

  public restoreAccount(userID: number, accountID: number): Observable<void> {
    return this._http.post<void>(
      accountURLs.restoreAccount(userID, accountID),
      {},
      {
        context: new HttpContext()
          .set(IS_PRIVATE_API, true)
          .set(IS_API_GATEWAY, true),
      },
    );
  }

  public restoreTPAccount(accountID: number): Observable<boolean> {
    return this._http.put<boolean>(
      accountURLs.restoreTPAccount(accountID),
      {},
      {
        headers: { 'X-Ray-Id': (+new Date()).toString() },
        context: new HttpContext().set(IS_PRIVATE_API, true),
      },
    );
  }

  public changeName(
    userID: number,
    accountID: number,
    name: string,
  ): Observable<void> {
    return this._http.put<void>(
      accountURLs.changeName(userID, accountID),
      {
        name: name.trim(),
      },
      {
        context: new HttpContext()
          .set(IS_PRIVATE_API, true)
          .set(IS_API_GATEWAY, true),
      },
    );
  }

  public changeComment(
    userID: number,
    accountID: number,
    comment: string,
  ): Observable<void> {
    return this._http.put<void>(
      accountURLs.changeComment(userID, accountID),
      {
        comment: comment.trim(),
      },
      {
        context: new HttpContext()
          .set(IS_PRIVATE_API, true)
          .set(IS_API_GATEWAY, true),
      },
    );
  }

  public setPrimaryAccount(
    userID: number,
    accountId: number,
  ): Observable<void> {
    return this._http.put<void>(
      accountURLs.setPrimaryAccount(userID),
      { accountId },
      {
        context: new HttpContext()
          .set(IS_PRIVATE_API, true)
          .set(IS_API_GATEWAY, true),
      },
    );
  }

  public getLeverageSettings(accountId: number): Observable<LeverageSettings> {
    return this._http
      .get<LeverageSettingsResponse>(accountURLs.leverageSettings(accountId), {
        context: new HttpContext()
          .set(IS_PRIVATE_API, true)
          .set(IS_API_GATEWAY, true),
      })
      .pipe(map(LeverageSettings.fromDTO));
  }

  public updateLeverageSettings(
    accountId: number,
    leverage: number,
  ): Observable<void> {
    return this._http.put<void>(
      accountURLs.leverageSettings(accountId),
      { leverage },
      {
        context: new HttpContext()
          .set(IS_PRIVATE_API, true)
          .set(IS_API_GATEWAY, true),
      },
    );
  }

  public updateSwapfreeSetting(
    userId: number,
    accountId: number,
    isEnabled: boolean,
  ): Observable<void> {
    return this._http.put<void>(
      accountURLs.swapfreeSettings(userId, accountId),
      { isEnabled },
      {
        context: new HttpContext().set(IS_PRIVATE_API, true),
      },
    );
  }

  public getSwapfreeSettings(
    userId: number,
    accountId: number,
  ): Observable<SwapfreeSettings> {
    return this._http.get<SwapfreeSettings>(
      accountURLs.swapfreeSettings(userId, accountId),
      { context: new HttpContext().set(IS_PRIVATE_API, true) },
    );
  }

  public changeMetaTraderPswd(
    userId: number,
    accountId: number,
    isInvestor = false,
  ): Observable<void> {
    return this._http.put<void>(
      accountURLs.changeMetaTraderPswd(userId, accountId),
      { isInvestor },
      {
        context: new HttpContext()
          .set(IS_PRIVATE_API, true)
          .set(IS_API_GATEWAY, true),
      },
    );
  }

  public addDemoFunds(
    userId: number,
    accountId: number,
    amount: number,
  ): Observable<void> {
    return this._http.post<void>(
      accountURLs.addDemoFunds(userId, accountId),
      { amount },
      {
        context: new HttpContext()
          .set(IS_PRIVATE_API, true)
          .set(IS_API_GATEWAY, true),
      },
    );
  }

  public openPartnerAccount(): Observable<{ id: string }> {
    return this._http.post<{ id: string }>(
      accountURLs.openPartner(),
      {},
      { context: new HttpContext().set(IS_PRIVATE_API, true) },
    );
  }

  public getPartnerState(id: number): Observable<PartnerState> {
    return this._http.get<PartnerState>(accountURLs.getPartnerState(id), {
      context: new HttpContext().set(IS_PRIVATE_API, true),
    });
  }

  public validatePartnerAttach(data: AttachPartnerRequest): Observable<void> {
    return this._http.get<void>(accountURLs.validatePartnerAttach(), {
      params: {
        accountLogin: data.accountLogin.toString(),
        partnerLogin: data.partnerLogin.toString(),
      },
      context: new HttpContext().set(IS_PRIVATE_API, true),
    });
  }

  public attachPartner(data: AttachPartnerRequest): Observable<void> {
    return this._http.post<void>(accountURLs.attachPartner(), data, {
      context: new HttpContext().set(IS_PRIVATE_API, true),
    });
  }
}
